/**
 * Fabbisogno — «cosa c'è da produrre, e cosa non riesco a calcolare».
 *
 * Tiene insieme tre cose che prima erano sparse nella tab «Evadibilità»: quanto
 * serve estrudere (l'esplosione dalle commesse), quanto il sistema **non** riesce
 * a esplodere (la mappatura PF↔semilavorato), e le date che promettiamo agli altri
 * moduli.
 *
 * Ci arrivano anche le due liste di anagrafica che stavano in Impostazioni:
 * Articoli e Semilavorati non sono manopole, sono **il vocabolario del
 * fabbisogno** — se un articolo non è mappato, il fabbisogno non lo vede. Stanno
 * accanto al numero che condizionano.
 */
import { AlertTriangle, Info, Loader2, RefreshCw } from "lucide-react";

import { RigaTesta } from "@/components/RigaTesta";
import {
  useM2Disponibilita,
  useM2Fabbisogno,
  useM2Impostazioni,
  useM2Riconciliazione,
  useM2RicalcolaDisponibilita,
} from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { fmtData, num } from "@/pages/M2/format";
import { EvadibilitaBadge, StampoChip } from "@/pages/M2/shared";
import type { RicettaFamigliaRead } from "@/types/m2";

const fmt = (n: number) => num(Math.round(n));

/** Il prefisso di famiglia dal codice semilavorato: `L-SP27-T208` → `SP`. */
function famigliaDi(codice: string): string | null {
  const m = /^L-([A-Za-z]+)/.exec(codice);
  return m ? m[1].toUpperCase() : null;
}

export function FabbisognoM2() {
  const fab = useM2Fabbisogno();
  const ric = useM2Riconciliazione();
  const imp = useM2Impostazioni();

  const semilav = fab.data?.semilavorati ?? [];
  const kgTot = semilav.reduce((a, s) => a + s.kg, 0);
  const nonMappati = fab.data?.non_mappati.length ?? 0;

  const velPerFamiglia = new Map<string, RicettaFamigliaRead>(
    (imp.data?.ricette_famiglie ?? []).map((f) => [f.prefisso, f])
  );

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto={`finestra di ${fab.data?.finestra_giorni ?? 21} giorni`}
        caricatoIl={fab.data?.snapshot_il}
        etichettaData="commesse dello snapshot del"
      >
        <span className="text-[11.5px] text-muted-foreground">
          esploso dalle commesse, non dal piano
        </span>
      </RigaTesta>

      {/*
        ⚠️ Nel modulo ci sono **due** fabbisogni e fino al 19-09-2026 nessuna
        delle due pagine nominava l'altra. Questo è l'esploso dalle commesse di
        M1; quello dell'MRP sta in «Caricamenti». Il 18-09 dicevano 768.865 kg e
        785.593 kg: distano il 2%, cioè abbastanza da sembrare lo stesso numero
        e abbastanza da non riconciliare mai. Chi legge deve sapere quale ha
        davanti prima di confrontarli con qualcosa.
      */}
      <div className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-2.5 text-[12px] text-muted-foreground">
        <Info className="size-4 shrink-0" />
        <div>
          Questo fabbisogno è <b>calcolato dal modulo</b> esplodendo le commesse di M1 sulla
          finestra qui sopra. Il fabbisogno che arriva dal gestionale — l&apos;<b>MRP</b> che
          carica Giacomo, su tutto il suo orizzonte — è un&apos;altra cosa e si legge in{" "}
          <b>Caricamenti</b>. I due numeri non devono coincidere.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Riquadro n={semilav.length} label="Semilavorati da fare" />
        <Riquadro n={kgTot} label="Chilogrammi" arrotonda />
        <Riquadro n={nonMappati} label="Articoli non mappati" allarme />
        <Riquadro
          n={ric.data?.pf_mappatura_pct ?? 0}
          suffisso="%"
          label="Copertura anagrafica"
          allarme={(ric.data?.pf_mappatura_pct ?? 0) < 50}
        />
      </div>

      {/* ── cosa serve estrudere ── */}
      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Cosa serve estrudere
        </h2>
        {fab.isLoading ? (
          <Caricamento />
        ) : semilav.length === 0 ? (
          <p className="rounded-lg border border-dashed bg-muted p-6 text-center text-sm text-muted-foreground">
            Nessun fabbisogno nella finestra.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left font-medium">Semilavorato</th>
                    <th className="px-3 py-2 text-left font-medium">Stampo</th>
                    <th className="px-3 py-2 text-left font-medium">Famiglia</th>
                    <th className="px-3 py-2 text-right font-medium">Fabbisogno</th>
                    <th className="px-3 py-2 text-right font-medium">Ore stimate</th>
                    <th className="px-3 py-2 text-left font-medium">Servirebbe entro</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {semilav.slice(0, 20).map((s) => {
                    const pref = famigliaDi(s.semilavorato_codice);
                    const fam = pref ? velPerFamiglia.get(pref) : undefined;
                    const vel = fam?.velocita_kg_h ?? null;
                    return (
                      <tr key={s.semilavorato_codice}>
                        <td className="px-3 py-1.5 font-mono text-[11.5px]">
                          {s.semilavorato_codice}
                        </td>
                        <td className="px-3 py-1.5">
                          <StampoChip stampo={s.semilavorato_codice.split("-").pop() ?? null} />
                        </td>
                        <td className="px-3 py-1.5 text-[11px]">
                          {pref && (
                            <span className="rounded border bg-secondary px-1.5 py-0.5">{pref}</span>
                          )}
                          {vel && (
                            <span className="ml-1 text-muted-foreground">
                              {num(vel)} kg/h
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                          {fmt(s.kg)} kg
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {vel ? `${Math.round(s.kg / vel)} h` : "—"}
                        </td>
                        {/* ⚠️ Grigio anche se la data è passata: poggia sulla data
                            di consegna delle commesse, che «viene messa solo per
                            far produrre». Stessa scelta presa in Commesse. */}
                        <td className="px-3 py-1.5 text-muted-foreground">
                          {fmtData(s.data_entro)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="mt-2 flex gap-2 text-[11px] text-muted-foreground">
          <Info className="size-3.5 shrink-0" />
          <span>
            <b>«Servirebbe entro» è scritto in grigio anche quando è passato.</b> Poggia sulla data di
            consegna delle commesse, che spesso non è una promessa vera — la mette il commerciale per
            far partire la produzione. Le ore sono una stima dalla velocità della famiglia.
          </span>
        </p>
      </section>

      {/* ── quello che non riesco a calcolare ── */}
      {ric.data && (
        <section>
          <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Quello che non riesco a calcolare
          </h2>
          <div className="space-y-3 rounded-lg border bg-card p-3.5">
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-xl font-semibold tabular-nums">
                  {ric.data.pf_mappatura_pct}%
                </div>
                <div className="text-[11px] text-muted-foreground">
                  articoli con semilavorato noto ({fmt(ric.data.pf_con_semilavorato)} su{" "}
                  {fmt(ric.data.pf_totali)})
                </div>
                <div className="mt-1.5 h-1.5 w-[210px] overflow-hidden rounded-sm bg-muted">
                  <span
                    className="block h-full bg-red-500"
                    style={{ width: `${Math.max(1, ric.data.pf_mappatura_pct)}%` }}
                  />
                </div>
              </div>
              <Mini label="dall'anagrafica" valore={ric.data.fonte_anagrafica} />
              <Mini label="dal piano di confezionamento" valore={ric.data.fonte_piano} />
              <Mini label="anomalie da guardare" valore={ric.data.anomalie.length} />
            </div>

            {ric.data.fonte_anagrafica === 0 && ric.data.fonte_piano > 0 && (
              <div className="flex gap-2.5 rounded-lg border border-brand-200 bg-accent px-3.5 py-2.5 text-[12.5px] text-accent-foreground dark:border-brand-800">
                <AlertTriangle className="size-4 shrink-0 text-primary" />
                <div>
                  <b>L&apos;anagrafica non porta la colonna «Semilavorato»</b>: tutti i legami noti
                  arrivano dal piano MES di confezionamento. Senza quella colonna il fabbisogno vede
                  solo gli articoli che compaiono in un piano — ed è il motivo per cui la copertura
                  è così bassa.
                </div>
              </div>
            )}

            {ric.data.anomalie.length > 0 && (
              <div>
                <div className="mb-1.5 text-[11px] text-muted-foreground">
                  Le prime anomalie, così come si leggono adesso:
                </div>
                <div className="overflow-hidden rounded-md border">
                  {ric.data.anomalie.slice(0, 8).map((a, i) => (
                    <div
                      key={`${a.codice}-${i}`}
                      className="grid grid-cols-[9rem_5rem_minmax(0,1fr)] items-baseline gap-2 border-t px-3 py-1.5 text-[12px] first:border-t-0"
                    >
                      <span className="text-[10.5px] text-muted-foreground">
                        {a.tipo.replace(/_/g, " ")}
                      </span>
                      <span className="font-mono text-[11px]">{a.codice}</span>
                      <span className="truncate text-muted-foreground">
                        {(a.descrizione ?? a.dettaglio ?? "").replace(/^§+\s*/, "")}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Fra queste ci sono materie prime (orzo, amidi) ed etichette: non sono prodotti
                  finiti e non avranno mai un semilavorato. Il campo per escluderle dalla
                  pianificazione <b>esiste già nel modello e non è mai stato riempito</b> — la spunta
                  che lo popola è il prossimo passo, e questa è la sezione dove vivrà.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      <Disponibilita />

      {/* ── anagrafica: arriva da Impostazioni, dove nessuno la cercava ── */}
      {imp.data && (
        <section>
          <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Anagrafica di pianificazione
          </h2>
          <div className="grid gap-2 lg:grid-cols-2">
            <div className="rounded-lg border bg-card p-3">
              <div className="text-[13px] font-semibold">Articoli</div>
              <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                {fmt(imp.data.articoli.coverage.totale)} · specie{" "}
                {imp.data.articoli.coverage.specie_pct}% · base proteica{" "}
                {imp.data.articoli.coverage.base_proteica_pct}% · stampo dal codice
              </div>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <div className="text-[13px] font-semibold">Semilavorati</div>
              <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                {imp.data.semilavorati.coverage.totale} ·{" "}
                {imp.data.semilavorati.coverage.stampi_distinti} stampi ·{" "}
                {imp.data.semilavorati.coverage.famiglie_distinte} famiglie-ricetta
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Le date verso gli altri moduli ───────────────────────────────────

function Disponibilita() {
  const disp = useM2Disponibilita();
  const ricalcola = useM2RicalcolaDisponibilita();
  const d = disp.data;
  const spenta = !d || d.righe === 0;

  return (
    <section>
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Le date che promettiamo agli altri moduli
      </h2>

      {spenta ? (
        <div className="flex flex-wrap items-start gap-3 rounded-lg border border-dashed bg-muted/50 p-3.5">
          <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">
            <b className="text-foreground">Non è ancora acceso.</b> La Pianificazione è la fonte
            della <i>data prevista di disponibilità</i> di ogni riga d&apos;ordine, ma finché questo
            calcolo è vuoto Commesse e Spedizioni usano il conto di M1 — e continuano a funzionare.
            <div className="mt-1">
              ⚠️ Accendendolo, i loro numeri cambiano di colpo, direzione compresa: è un gesto da
              concordare, non da provare.
            </div>
          </div>
          <button
            type="button"
            onClick={() => ricalcola.mutate()}
            disabled={ricalcola.isPending}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border bg-card px-3 py-2 text-[12.5px] font-medium hover:bg-accent/40 disabled:opacity-50"
          >
            {ricalcola.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Calcola le date
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <Riquadro n={d.righe} label="Righe valutate" />
            <Riquadro n={d.per_stato["puntuale"] ?? 0} label="Puntuali" tono="text-emerald-700" />
            <Riquadro n={d.per_stato["in_ritardo"] ?? 0} label="In ritardo" tono="text-red-700" />
            <Riquadro n={d.per_stato["a_rischio"] ?? 0} label="A rischio" tono="text-amber-700" />
            <Riquadro
              n={d.per_stato["inevadibile"] ?? 0}
              label="Inevadibili"
              tono="text-muted-foreground"
            />
          </div>
          {d.discordanze_bc > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="size-4 shrink-0" />
              <span>
                <b>{d.discordanze_bc}</b> righe in cui la nostra previsione discorda dallo stato del
                gestionale.
              </span>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left font-medium">Ordine</th>
                    <th className="px-3 py-2 text-left font-medium">Articolo</th>
                    <th className="px-3 py-2 text-left font-medium">Consegna</th>
                    <th className="px-3 py-2 text-left font-medium">Disp. prevista</th>
                    <th className="px-3 py-2 text-left font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {d.items.slice(0, 12).map((r) => (
                    <tr key={r.ordine_riga_id ?? `${r.numero_ordine}-${r.codice_articolo}`}>
                      <td className="px-3 py-1.5 font-medium text-primary">
                        #{r.numero_ordine ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.codice_articolo}</td>
                      <td className="px-3 py-1.5 tabular-nums">{fmtData(r.data_consegna)}</td>
                      <td className="px-3 py-1.5 tabular-nums">
                        {fmtData(r.data_prevista_disponibilita)}
                      </td>
                      <td className="px-3 py-1.5">
                        <EvadibilitaBadge stato={r.stato} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Riquadro({
  n,
  label,
  suffisso,
  allarme,
  arrotonda,
  tono,
}: {
  n: number;
  label: string;
  suffisso?: string;
  allarme?: boolean;
  arrotonda?: boolean;
  tono?: string;
}) {
  const testo = arrotonda
    ? n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1).replace(".", ",")} M`
      : fmt(n)
    : fmt(n);
  return (
    <div className="rounded-lg border bg-card px-3 py-2">
      <div
        className={cn(
          "text-xl font-semibold leading-tight tabular-nums",
          tono ?? (allarme && n > 0 ? "text-red-700 dark:text-red-400" : undefined)
        )}
      >
        {testo}
        {suffisso}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Mini({ label, valore }: { label: string; valore: number }) {
  return (
    <div className="min-w-[92px]">
      <div className="text-xl font-semibold tabular-nums">{fmt(valore)}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Caricamento() {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Caricamento…
    </div>
  );
}
